import { Divider, Grid, InputAdornment } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import MuiToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import {
  AutoComplete,
  Dialog,
  Icons,
  Select,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { emailForAdhocTaskBulkReassign } from '../../graphql/Email';
import { bulkAssignOrReassign } from '../../graphql/InspectorOperations';
import { useMst } from '../../mobx-models/Root';
import { IFieldInspectorList } from '../../models/models';
import useBulkSelectedTask from '../../rxdb/taskList/useBulkSelectedTask';
import { useRxDB } from '../../rxdb/useRxDB';
import { isJestTest } from '../../test-helpers/isJestTest';
import { mockFieldInspectorList, mockSPList } from '../../test-helpers/mockData/assignInspector';
import { mockFIList } from '../../test-helpers/mockData/viewInspectors';
import { useNotification } from '../../utilities';
import { INSPECTION_LENGTH_WHILE_BULK } from '../../utilities/constants';
import { BatchSize, FieldInspectorType, InspectorRole, TaskStatus } from '../../utilities/enums';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import CustomButton from '../CustomButton/CustomButton';
import StyledButton from '../InspectorInfo/StyledButton';
import useAddInspectorStyles from './AddInspectors.styles';
import useReAssignStyles from './ReAssignInspector.styles';

const ToggleButton = styled(MuiToggleButton)(({}) => ({
  '&.Mui-selected:not(.Mui-disabled), &.Mui-selected:hover:not(.Mui-disabled)': {
    color: '#121212',
    backgroundColor: 'rgba(42, 171, 63, 1)',
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    color: '#121212',
    backgroundColor: 'dimgray',
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  taskIds: number[];
}

const AdhocBulkInspectionReAssignment: React.FC<Props> = ({ open, onClose, taskIds }: Props) => {
  const reAssignClasses = useReAssignStyles();
  const addInspectorClasses = useAddInspectorStyles();
  const { primaryContext, CurrentUserSelected, SiteDiscipline, HandleGesture } = useMst();
  const { db } = useRxDB();
  const { successNotification, errorNotification } = useNotification();
  const { taskList } = useBulkSelectedTask(taskIds);
  const loggedInUser = CurrentUserSelected?.currentUser;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [description, setDescription] = useState('');
  const [inspectorType, setInspectorType] = useState<string>('Nutrien');
  const [serviceProviderId, setServiceProviderId] = useState<number>(1);
  const [selectedInspector, setSelectedInspector] = useState<{
    fieldInspectorId?: number;
    fieldInspectorName?: string;
    clerkId?: number;
    clerkName?: string;
  } | null>(null);
  const [isDECView, setIsDECView] = useState(false);
  const [disableToggleButton] = useState(false);
  const [fieldInspectorType, setFieldInspectorType] = useState<string>(FieldInspectorType.FI);
  const isDECUser = !!loggedInUser?.clerkId;

  const allFieldInspectorLIst = isJestTest()
    ? mockFieldInspectorList
    : isDECView
    ? primaryContext.dataEntryClerksList.toJSON()
    : primaryContext.fieldInspectorList.toJSON();

  const assignedInspectors = [...allFieldInspectorLIst]?.filter(e =>
    taskList.find(task => task.fkInspectorField === e.pkInspector || task.clerkId === e.clerkId),
  );

  const handleMainToggleChange = (
    _event: React.MouseEvent<HTMLElement, MouseEvent>,
    inspType: string,
  ) => {
    if (inspType !== null) {
      setFieldInspectorType(inspType);
    }
  };

  const onCommentsInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    setDescription(value);
  };

  const onAssign = async () => {
    setIsLoading(true);
    const isReassignmentByFI = loggedInUser?.inspectorId !== taskList[0].fkInspectorLead;
    const mobileFieldInspectorInspections = {
      pkAssetInspectionTask: 1,
      fkSchedule: CurrentUserSelected.workPackageId,
      fkInspectorLead: taskList[0].fkInspectorLead,
      fkInspectorField: Number(selectedInspector?.fieldInspectorId),
      clerkId: Number(selectedInspector?.clerkId),
      taskDescription: description,
      isReviewedByLI: false,
      isReviewedByFI: false,
      commentInspectorLead: '',
      commentInspectorField: '',
      site: SiteDiscipline.siteId,
      discipline: SiteDiscipline.disciplineId,
      isActive: true,
      fkInspectionTaskStatusCategory: TaskStatus.ASSIGNED,
      pkUuId: uuidv4(),
      recommendations: '',
      dateCompleted: null,
      isPerformedByFI: isReassignmentByFI,
      isBulkReAssignment: true,
    };
    const role = isReassignmentByFI
      ? !isDECView
        ? InspectorRole.FIELD
        : InspectorRole.DEC
      : InspectorRole.LEAD;

    const varialblesList = [];
    let position = 0;
    while (position < taskList.length) {
      const batch = taskList.slice(position, position + INSPECTION_LENGTH_WHILE_BULK);
      const inspectionIdList: number[] = [];
      batch.forEach(task => {
        if (!inspectionIdList.includes(task.fkAssetInspection))
          inspectionIdList.push(task.fkAssetInspection);
      });
      const taskIdList = batch.map(task => task.pkAssetInspectionTask);
      varialblesList.push({
        mobileFieldInspectorInspections,
        role,
        inspectionIdList,
        taskIdList,
      });
      position += INSPECTION_LENGTH_WHILE_BULK;
    }

    try {
      position = 0;
      let results: unknown[] = [];
      while (position < varialblesList.length) {
        const itemsForBatch = varialblesList.slice(position, position + BatchSize.RE_ASSIGN);
        results = [
          ...results,
          ...(await Promise.all(
            itemsForBatch.map(async variables => {
              try {
                await bulkAssignOrReassign(variables);
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
                // console logs will be removed once sentry implementation is done
                sentryCaptureException(
                  error,
                  '~ file: AssignToMeDialog.tsx ~ line 120 ~ saveBulkTaskOnInspection ~',
                  'bulkOperation',
                  JSON.stringify({ ...variables, error: JSON.stringify(error) }),
                );
              }
            }),
          )),
        ];
        position += BatchSize.RE_ASSIGN;
      }
      if (results.length === varialblesList.length) {
        try {
          const bulkTaskandInspectionsList = HandleGesture.tasksforReAssignAndSign
            .toJSON()
            .map(task => {
              return {
                inspectionId: task.inspectionId,
                taskId: task.taskId,
                previousFI: task.fieldInspectorId,
                leadInspectorId: task.leadInspectorId,
                isActionByFI: CurrentUserSelected.currentUser?.inspectorId !== task.leadInspectorId,
              };
            });
          if (bulkTaskandInspectionsList?.length > 0) {
            await emailForAdhocTaskBulkReassign(
              SiteDiscipline.siteId,
              Number(selectedInspector?.fieldInspectorId || 0),
              Number(selectedInspector?.clerkId || 0),
              bulkTaskandInspectionsList,
            );
          }
        } catch (error) {
          sentryCaptureException(
            error,
            '~ file: AdhocBulkInspectionReAssignment.tsx ~ line 135 ~ emailForAdhocTaskBulkReassign',
          );
        } finally {
          db?.startReplication();
          successNotification(
            `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} re-assigned Successfully`,
          );
          setIsLoading(false);
          HandleGesture.resetEverything();
          onClose();
          HandleGesture.setIsCompleted(true);
          HandleGesture.setRunOnTaskSubscription(true);
          const siteId = SiteDiscipline.siteId;
          const disciplineId = SiteDiscipline.disciplineId;
          primaryContext.getwpFilesFromMobx(siteId, disciplineId);
          primaryContext.getWorkPackageFromMobx(siteId, disciplineId);
          primaryContext.getInspectionListFromMobx(siteId, disciplineId);
        }
      } else {
        throw new Error('FI Not Updated');
      }
    } catch (error) {
      errorNotification(
        `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} is not updated please try again`,
      );
      setIsLoading(false);
      HandleGesture.resetEverything();
      sentryCaptureException(
        error,
        '~ file: AdhocBulkInspectionReAssignment.tsx ~ line 135 ~ onAssign',
      );
    }
  };

  const handleSearch = (
    event: React.KeyboardEvent<HTMLDivElement>,
    inspector: IFieldInspectorList,
  ) => {
    if (!inspector) {
      setSelectedInspector(null);
      return;
    }
    if (
      event.key === 'Enter' &&
      (selectedInspector?.fieldInspectorId || selectedInspector?.clerkId)
    ) {
      onAssign();
      return;
    }
    const name =
      inspector?.pkInspector === loggedInUser?.inspectorId
        ? loggedInUser?.name
        : `${inspector?.firstName} ${inspector?.lastName}`;

    setSelectedInspector({
      fieldInspectorId: inspector?.pkInspector || 0,
      fieldInspectorName: name,
      clerkId: inspector?.clerkId || 0,
      clerkName: inspector?.clerkName,
    });
  };

  const handleOnSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedInspector?.fieldInspectorId || selectedInspector?.clerkId) {
      onAssign();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'Enter' &&
      (selectedInspector?.fieldInspectorId || selectedInspector?.clerkId)
    ) {
      onAssign();
    }
  };

  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    inspType: string,
  ) => {
    if (inspType !== null) {
      if (inspType === 'Nutrien') {
        setServiceProviderId(1);
      }
      setInspectorType(inspType);
    }
  };

  const assignedFilteredInspectors = useMemo(() => {
    return assignedInspectors?.filter(item => {
      if (fieldInspectorType === FieldInspectorType.DEC) {
        return item.clerkId;
      } else {
        return true;
      }
    });
  }, [assignedInspectors, fieldInspectorType]);

  const inspectorList = useMemo(() => {
    if (allFieldInspectorLIst?.length) {
      return [...allFieldInspectorLIst]
        ?.filter(inspector => {
          if (inspector?.fkServiceProvider !== serviceProviderId || !inspector?.isActive) {
            return false;
          }

          if (assignedInspectors.length === 0) {
            return true;
          }

          return assignedInspectors?.find(taskInspector => {
            if (!isDECView) {
              return (
                taskInspector.pkInspector && taskInspector.pkInspector !== inspector.pkInspector
              );
            } else {
              return taskInspector.clerkId !== inspector.clerkId;
            }
          });
        })
        ?.sort((a, b) =>
          a.pkInspector == loggedInUser?.inspectorId
            ? -1
            : b.pkInspector == loggedInUser?.inspectorId
            ? 1
            : 0,
        )
        .map(inspector => {
          if (inspector.pkInspector === loggedInUser?.inspectorId) {
            return { ...inspector, name: 'Assigned To Me' };
          }
          return { ...inspector, name: `${inspector.firstName} ${inspector.lastName}` };
        });
    }
    return [];
  }, [serviceProviderId, allFieldInspectorLIst, selectedInspector, assignedInspectors]);

  useEffect(() => {
    let isMounted = true;
    const getMasterData = async () => {
      try {
        await Promise.all([
          primaryContext.getFIListFromMobx(),
          primaryContext.getSPListFromMobx(),
          primaryContext.getDataEntryClerksFromMobx(),
        ]);
      } catch (e) {
        if (isMounted) errorNotification('data is not loaded');
      }
    };
    getMasterData();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isDECUser) {
      const isDECViewSelected = fieldInspectorType === FieldInspectorType.DEC;
      setIsDECView(isDECViewSelected);
    }
  }, [fieldInspectorType, selectedInspector]);

  const getDivider = () => {
    return <Divider orientation="horizontal" className={reAssignClasses.divider} />;
  };

  return (
    <>
      <Dialog
        open={open}
        titleText={`Re-Assign ${!isDECView ? 'Inspector' : 'Data Entry Clerk'}`}
        onClose={onClose}
        backButton={false}
        dialogActions={
          <CustomButton
            isLoading={isLoading}
            onClose={onAssign}
            disabled={!selectedInspector}
            buttonText="Re-Assign"
            dataTestId="reAssignButton"
          />
        }
        maxWidth="xs"
        noMinHeight
      >
        <form onSubmit={handleOnSubmit}>
          <Grid container className={reAssignClasses.root}>
            <Grid item container xs={12} alignItems="center">
              <ToggleButtonGroup
                color="#121212"
                value={fieldInspectorType}
                exclusive
                onChange={handleMainToggleChange}
              >
                <ToggleButton
                  disabled={disableToggleButton}
                  value={FieldInspectorType.FI}
                  data-testid="nutrientogglebutton"
                >
                  Field Inspector
                </ToggleButton>
                <ToggleButton
                  disabled={disableToggleButton}
                  value={FieldInspectorType.DEC}
                  data-testid="sptogglebutton"
                >
                  Data Entry Clerk
                </ToggleButton>
              </ToggleButtonGroup>
              {getDivider()}
              {assignedFilteredInspectors && assignedFilteredInspectors.length > 0 && (
                <>
                  <Grid item container alignItems="center">
                    <Typography variant="body2" className={reAssignClasses.nameLabel}>
                      Assigned {!isDECView ? 'Inspector' : 'Data Entry Clerk'}
                    </Typography>
                  </Grid>
                  <Grid item container>
                    {assignedFilteredInspectors?.map(inspector => (
                      <StyledButton
                        inspector={inspector}
                        canDelete={false}
                        key={inspector.pkInspector}
                      />
                    ))}
                  </Grid>
                  {getDivider()}
                </>
              )}
            </Grid>

            <Grid item xs={12}>
              <ToggleButtonGroup
                color="#121212"
                value={inspectorType}
                exclusive
                onChange={handleToggleChange}
              >
                <ToggleButton value="Nutrien" data-testid="nutrienToggleButton">
                  Nutrien
                </ToggleButton>
                <ToggleButton value="ServiceProvider" data-testid="serviceproviderToggleButton">
                  Service Provider
                </ToggleButton>
              </ToggleButtonGroup>
              {inspectorType === 'ServiceProvider' && (
                <Grid className={addInspectorClasses.spSelectGrid} item xs={12}>
                  <Select
                    className={addInspectorClasses.spSelect}
                    label="Service Provider"
                    list={
                      isJestTest()
                        ? mockSPList
                        : primaryContext.serviceProviderList.filter(e => e.pkServiceProvider !== 1)
                    }
                    getOptionLabel={list => `${list.organizationName}`}
                    getOptionValue={list => list.pkServiceProvider}
                    onChange={event => setServiceProviderId(event.target.value)}
                    defaultValue={serviceProviderId}
                    noMinHeight
                    data-testid="splistoptions"
                  />
                </Grid>
              )}

              <Grid item className={addInspectorClasses.autoCompleteGrid}>
                <AutoComplete
                  autoSelect={false}
                  key="field-inspector"
                  noMinHeight
                  size="small"
                  label={!isDECView ? 'Field Inspector' : 'Data Entry Clerk'}
                  closeIcon=""
                  startAdornment={
                    <InputAdornment position="start">
                      <Icons.SearchFeather color="rgba(149, 149, 149, 1)" height={20} width={20} />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <Icons.PlusCircleFeather
                        className={addInspectorClasses.plusIcon}
                        color="primary"
                        strokeWidth="1.5px"
                      />
                    </InputAdornment>
                  }
                  placeholder={`Search ${!isDECView ? 'Inspector' : 'Data Entry Clerk'}`}
                  onChange={handleSearch}
                  list={
                    isJestTest()
                      ? mockFIList
                      : inspectorList?.sort((a, b) =>
                          a?.name?.toLowerCase() < b?.name?.toLowerCase() ? -1 : 1,
                        )
                  }
                  value={inspectorList.find(
                    inspector =>
                      inspector.pkInspector === selectedInspector?.fieldInspectorId ||
                      inspector.clerkId === selectedInspector?.clerkId,
                  )}
                  getOptionLabel={option => (option ? option.name : option)}
                  data-testid="fieldInspectorInput"
                />
              </Grid>
            </Grid>

            <Grid item container className={reAssignClasses.commentPadding}>
              <Typography variant="subtitle2" className="reassignInspectorLabel">
                Comments
              </Typography>
              <TextField
                defaultValue={description}
                onChange={onCommentsInput}
                onKeyDown={handleKeyDown}
                noMinHeight
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Type"
                multiline
                rows={3}
                data-testid="bulkReassign-comments"
              />
            </Grid>
          </Grid>
        </form>
      </Dialog>
    </>
  );
};

export default observer(AdhocBulkInspectionReAssignment);
